import { ENEMIES, type EnemyDefinition, type EnemyId } from '@/data/enemies';
import { ComponentStore, type Entity, EntityWorld } from '@/ecs/world';
import { TILE_SIZE } from '@/sprites/tiles';
import type { Tilemap } from '@/world/tilemap';
import { type Aabb, overlaps } from '@/combat/swing';
import { SAP_SECONDS, SAP_SPEED } from '@/data/abilities';

/**
 * Enemies: where they are, what they are doing, and what happens when they are
 * hit.
 *
 * Deliberately free of rendering and input. Everything here can be stepped
 * with a delta and inspected, which is what makes a fight testable at all —
 * "does the moth close in and die to two swings" is a question about numbers,
 * not about pixels.
 */

/** Enemies are smaller than their 16px sprite, so near misses read as misses. */
const HURTBOX = 10;

export interface EnemyState {
  readonly id: EnemyId;
  readonly definition: EnemyDefinition;
  x: number;
  y: number;
  health: number;
  /** Seconds left before it can deal contact damage again. */
  contactTimer: number;
  /** Seconds left of the white flash after being hit. */
  flash: number;
  /** Seconds left of being sapped, which is the Sapping ability's whole effect. */
  slow: number;
  /** Knockback velocity, decaying. */
  pushX: number;
  pushY: number;
  /** Where it wanders back to when it loses interest. */
  readonly homeX: number;
  readonly homeY: number;
}

export interface HitResult {
  readonly entity: Entity;
  readonly state: EnemyState;
  readonly damage: number;
  readonly killed: boolean;
}

/** How fast knockback bleeds off, as a fraction remaining per second. */
const FRICTION = 0.0001;

export class EnemyForce {
  private readonly world = new EntityWorld();
  readonly states: ComponentStore<EnemyState>;
  /** Alternated on a shared clock: a field of moths needs one animation timer. */
  private elapsed = 0;

  constructor() {
    this.states = this.world.register(new ComponentStore<EnemyState>());
  }

  get count(): number {
    return this.states.size;
  }

  get frame(): 0 | 1 {
    return Math.floor(this.elapsed * 4) % 2 === 0 ? 0 : 1;
  }

  spawn(id: EnemyId, x: number, y: number): Entity {
    const entity = this.world.create();
    const definition = ENEMIES[id];
    this.states.set(entity, {
      id,
      definition,
      x,
      y,
      health: definition.maxHealth,
      contactTimer: 0,
      flash: 0,
      slow: 0,
      pushX: 0,
      pushY: 0,
      homeX: x,
      homeY: y,
    });
    return entity;
  }

  clear(): void {
    this.world.clear();
  }

  all(): [Entity, EnemyState][] {
    return this.states.entries();
  }

  /**
   * Chase, drift home, and settle knockback.
   *
   * Walls are respected on each axis separately so an enemy sliding along a
   * fence keeps moving rather than sticking to it — the same trick the player
   * controller uses.
   */
  update(dt: number, targetX: number, targetY: number, map: Tilemap): void {
    this.elapsed += dt;

    for (const [, state] of this.states.entries()) {
      state.contactTimer = Math.max(0, state.contactTimer - dt);
      state.flash = Math.max(0, state.flash - dt);
      state.slow = Math.max(0, state.slow - dt);

      const speed = state.definition.speed * (state.slow > 0 ? SAP_SPEED : 1);

      const dx = targetX - state.x;
      const dy = targetY - state.y;
      const distance = Math.hypot(dx, dy);

      let moveX = 0;
      let moveY = 0;

      if (distance <= state.definition.aggroRange && distance > 1) {
        moveX = (dx / distance) * speed;
        moveY = (dy / distance) * speed;
      } else {
        // Out of range: amble back towards where it started, so a chase that
        // is broken off does not leave enemies piled in a corner of the map.
        const homeX = state.homeX - state.x;
        const homeY = state.homeY - state.y;
        const homeDistance = Math.hypot(homeX, homeY);
        if (homeDistance > TILE_SIZE / 2) {
          moveX = (homeX / homeDistance) * speed * 0.4;
          moveY = (homeY / homeDistance) * speed * 0.4;
        }
      }

      const decay = Math.pow(FRICTION, dt);
      moveX += state.pushX;
      moveY += state.pushY;
      state.pushX *= decay;
      state.pushY *= decay;

      this.step(state, moveX * dt, moveY * dt, map);
    }
  }

  private step(state: EnemyState, dx: number, dy: number, map: Tilemap): void {
    const half = HURTBOX / 2;

    const nextX = state.x + dx;
    if (!this.blocked(nextX, state.y, half, map)) state.x = nextX;

    const nextY = state.y + dy;
    if (!this.blocked(state.x, nextY, half, map)) state.y = nextY;
  }

  private blocked(centreX: number, centreY: number, half: number, map: Tilemap): boolean {
    return map.collides({
      x: centreX - half,
      y: centreY - half,
      width: half * 2,
      height: half * 2,
    });
  }

  hurtbox(state: EnemyState): Aabb {
    return {
      x: state.x - HURTBOX / 2,
      y: state.y - HURTBOX / 2,
      width: HURTBOX,
      height: HURTBOX,
    };
  }

  /**
   * Apply a swing.
   *
   * Everything the arc covers is hit at once — a swing is a swing, not a
   * search for the nearest target — and the dead are removed here so callers
   * never hold a reference to a corpse.
   *
   * `alreadyHit` carries a swing's memory between steps, so one swing lands
   * one hit per enemy however long its arc lingers.
   */
  resolveHit(
    arc: Aabb,
    damage: number,
    fromX: number,
    fromY: number,
    alreadyHit?: Set<number>,
    sapping = false,
  ): HitResult[] {
    const results: HitResult[] = [];

    for (const [entity, state] of this.states.entries()) {
      if (alreadyHit?.has(entity)) continue;
      if (!overlaps(arc, this.hurtbox(state))) continue;
      alreadyHit?.add(entity);

      state.health -= damage;
      state.flash = 0.12;
      if (sapping) state.slow = SAP_SECONDS;

      const dx = state.x - fromX;
      const dy = state.y - fromY;
      const distance = Math.hypot(dx, dy) || 1;
      const push = state.definition.knockback * 8;
      state.pushX = (dx / distance) * push;
      state.pushY = (dy / distance) * push;

      const killed = state.health <= 0;
      results.push({ entity, state, damage, killed });
      if (killed) this.world.destroy(entity);
    }

    return results;
  }

  /**
   * Damage from walking into things, for enemies off their cooldown. Starts
   * their cooldown, since asking is the same as being hit.
   *
   * The worst single hit, not the sum. Being surrounded should mean no safe
   * direction to retreat in, which it already does — it should not also mean
   * three enemies landing one blow each in the same instant, which turns a
   * crowd from dangerous into instantly fatal and is unreadable besides: the
   * player sees one flash and two thirds of their health gone.
   */
  contactDamage(target: Aabb): number {
    let worst = 0;

    for (const [, state] of this.states.entries()) {
      if (state.contactTimer > 0) continue;
      if (!overlaps(target, this.hurtbox(state))) continue;

      worst = Math.max(worst, state.definition.contactDamage);
      state.contactTimer = state.definition.contactCooldown;
    }

    return worst;
  }
}
